import { Box, type SxProps, type Theme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export interface LazyThumbnailProps {
  /** Preferred thumbnail URL; falls back to {@link fallbackSrc} when absent. */
  src?: string | null;
  /** Larger source used until/unless the thumbnail is available. */
  fallbackSrc?: string | null;
  alt?: string;
  sx?: SxProps<Theme>;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
  /** Rendered while no image source is available (e.g. "No Art" placeholder). */
  placeholder?: React.ReactNode;
  /** Fade-in duration in ms. */
  fadeMs?: number;
  /** IntersectionObserver rootMargin — pre-loads slightly before entering viewport. */
  rootMargin?: string;
}

export const LazyThumbnail = ({
  src,
  fallbackSrc,
  alt,
  sx,
  className,
  onClick,
  placeholder,
  fadeMs = 250,
  rootMargin = '200px',
}: LazyThumbnailProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const effectiveSrc = src ?? fallbackSrc ?? null;

  useEffect(() => {
    if (inView) return;
    const node = containerRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [inView, rootMargin]);

  useEffect(() => {
    setLoaded(false);
  }, [effectiveSrc]);

  if (!effectiveSrc) {
    return (
      <Box ref={containerRef} sx={sx} className={className} onClick={onClick}>
        {placeholder}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={sx}
      className={className}
      onClick={onClick}
      style={{ position: 'relative', overflow: 'hidden' }}
    >
      {inView && (
        <Box
          component="img"
          src={effectiveSrc}
          alt={alt}
          loading="lazy"
          decoding="async"
          onLoad={() => {
            setLoaded(true);
          }}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
            opacity: loaded ? 1 : 0,
            transition: `opacity ${String(fadeMs)}ms ease-in`,
          }}
        />
      )}
    </Box>
  );
};
